// AuthContext V2.2 Patch — Web-Safe & iOS Google OAuth Fix

// --- OAUTH STATE HELPERS (iOS Google Fix) ---
function genState(n = 32) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let s = ''
  for (let i = 0; i < n; i++) s += chars[Math.floor(Math.random() * chars.length)]
  return s
}
const OAUTH_STATE_KEY = 'oauth:state'
const setState = (s: string) => sessionStorage.setItem(OAUTH_STATE_KEY, s)
const getState = () => sessionStorage.getItem(OAUTH_STATE_KEY)
const clearState = () => sessionStorage.removeItem(OAUTH_STATE_KEY)

import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { AppLauncher } from '@capacitor/app-launcher'
import { Capacitor } from '@capacitor/core'
import { SessionContextProvider, useSessionContext, useUser as useSupaUser } from '@supabase/auth-helpers-react'
import { Provider, AuthError } from '@supabase/supabase-js'
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { useInAppBrowser } from '@/hooks/useInAppBrowser'
import { supabase } from '@/integrations/supabase/client'
import { callFunction } from '@/integrations/supabase/functions'
import type { Database } from '@/integrations/supabase/types'
import { IAP_ENABLED } from '@/lib/env'
import { logAuthEvent, generateCorrelationId } from '@/lib/logging'
import { Purchases, isConfigured } from '@/lib/plugins/revenuecat'
import { signInWithAppleSmart } from '@/lib/auth/apple'
import { useAuthReady } from './hooks/useAuthReady'
import { useProfileLoader } from './hooks/useProfileLoader'
import type { AuthContextType } from './types'

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <SessionContextProvider supabaseClient={supabase as any}>
    <InnerAuthProvider>{children}</InnerAuthProvider>
  </SessionContextProvider>
)

const isIOS = () => Capacitor.getPlatform?.() === 'ios'
const isSafariEngine = () => {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /safari/i.test(ua) && !/chrome|crios|fxios|edgios/i.test(ua)
}

async function triggerRevenueCatSync() {
  try {
    await callFunction('revenuecat-sync')
  } catch (e) {
    console.warn('[AuthContext][RC] revenuecat-sync failed', e)
  }
}

const InnerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, isLoading: loadingSession } = useSessionContext()
  const supaUser = useSupaUser()
  const [correlationId] = useState(() => generateCorrelationId())
  const [authInProgress, setAuthInProgress] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const { openInAppBrowser } = useInAppBrowser()
  const navigate = useNavigate()
  const isAuthReady = useAuthReady(loadingSession)
  const { profile, roles, isLoadingProfile, refreshProfile, isAdminUser } =
    useProfileLoader(session, correlationId)

  const isAuthenticated = Boolean(session?.user)
  const isLoading = Boolean(!isAuthReady || (session?.user && !profile && isLoadingProfile))
  const rcConfiguredRef = useRef(false)

  useEffect(() => {
    if (!isIOS()) return
    if (!IAP_ENABLED) return
    if (!session?.user?.id) return
    ;(async () => {
      try {
        if (!isConfigured()) {
          console.warn('[AuthContext][RC] RevenueCat not configured, skipping logIn')
          return
        }
        console.log('[AuthContext][RC] logIn', session.user!.id)
        await Purchases.logIn({ appUserID: session.user!.id })
        await Purchases.setAttributes({ $email: session.user!.email ?? '' })
        await triggerRevenueCatSync()
        rcConfiguredRef.current = true
      } catch (e) {
        console.warn('[AuthContext][RC] logIn failed', e)
      }
    })()
  }, [session?.user?.id])

  // ✅ PATCHED PKCE handler: only runs on /auth/callback
  useEffect(() => {
    if (window.location.pathname !== '/auth/callback') return;

    const url = new URL(window.location.href);
    const hasCode = url.searchParams.has('code');

    if (hasCode) {
      supabase.auth.exchangeCodeForSession(url.toString())
        .then(() => {
          window.history.replaceState({}, document.title, '/');
          navigate('/profile', { replace: true });
        })
        .catch((e) => {
          console.error('[PKCE Handler] Code exchange failed', e);
          toast.error('Authentication failed. Please try again.');
        });
    }
  }, [navigate]);

  useEffect(() => {
    if (!isIOS()) return
    console.log('[AuthContext][iOS] Registering appUrlOpen listener…')

    let sub: { remove: () => void } | undefined
    ;(async () => {
      try {
        sub = await App.addListener('appUrlOpen', async ({ url }) => {
          try {
            console.log('[AuthContext][iOS] appUrlOpen URL (full):', url)
            if (!url || !url.startsWith('prayai://auth/callback')) return

            try { await Browser.close() } catch {}

            const u = new URL(url)
            const hash = u.hash?.replace(/^#/, '') ?? ''
            const qp = new URLSearchParams(u.search ?? '')
            const hp = new URLSearchParams(hash)

            // Verify OAuth state for iOS Google to prevent replay attacks
            const returnedState = hp.get('state') || qp.get('state')
            if (returnedState && getState() && returnedState !== getState()) {
              console.error('[AuthContext][iOS] OAuth state mismatch; rejecting callback')
              clearState()
              toast.error('Authentication failed. Please try again.')
              setAuthInProgress(false)
              return
            }
            clearState()

            const access_token = hp.get('access_token') || qp.get('access_token')
            const refresh_token = hp.get('refresh_token') || qp.get('refresh_token')
            const code = hp.get('code') || qp.get('code')

            if (access_token && refresh_token) {
              console.log('[AuthContext][iOS] setSession from tokens (implicit)')
              await supabase.auth.setSession({ access_token, refresh_token })
            } else if (code) {
              console.log('[AuthContext][iOS] exchangeCodeForSession (pkce)')
              await supabase.auth.exchangeCodeForSession(url)
            } else {
              console.warn('[AuthContext][iOS] No tokens or code in callback URL')
              toast.error('Authentication incomplete. Please try again.')
              return
            }

            const { data: { session: currentSession } } = await supabase.auth.getSession()
            if (!currentSession?.user) {
              console.error('[AuthContext][iOS] No active session after auth callback')
              toast.error('Authentication failed. Please try again.')
              return
            }
            console.log('[AuthContext][iOS] Session verified, user:', currentSession.user.id)
            
            // Wait for profile refresh to complete before navigation
            try {
              await refreshProfile()
              console.log('[AuthContext][iOS] Profile refresh completed successfully')
            } catch (refreshError) {
              console.error('[AuthContext][iOS] Profile refresh failed:', refreshError)
              // Continue with navigation even if profile refresh fails
            }
            
            // Add small delay to ensure UI state is updated
            await new Promise(resolve => setTimeout(resolve, 500))
            
            try { navigate('/prayers', { replace: true }) } catch {}
          } catch (err) {
            console.error('[AuthContext][iOS] appUrlOpen error:', err)
          } finally {
            setAuthInProgress(false)
          }
        })
      } catch (listenerErr) {
        console.error('[AuthContext][iOS] Failed to register appUrlOpen listener:', listenerErr)
      }
    })()
    return () => { try { sub?.remove() } catch {} }
  }, [navigate, refreshProfile])

  // Universal auth stabilization - iOS resume listener
  useEffect(() => {
    if (!isIOS()) return
    let sub: { remove: () => void } | undefined
    ;(async () => {
      try {
        sub = await App.addListener('resume', async () => {
          if (!authInProgress) return
          await new Promise(r => setTimeout(r, 1000))
          const { data } = await supabase.auth.getSession()
          if (data?.session) setAuthInProgress(false)
        })
      } catch (error) {
        console.warn('[AuthContext][iOS] Resume listener setup failed:', error)
      }
    })()
    return () => { try { sub?.remove() } catch {} }
  }, [authInProgress])

  const login = async (email: string, password: string) => {
    const loginCorrelationId = generateCorrelationId()
    logAuthEvent('sign_in_attempt', { email, correlationId: loginCorrelationId })
    const normalizedEmail = email.toLowerCase().trim()
    const { data, error } = await supabase.auth.signInWithPassword({ email: normalizedEmail, password })
    if (error) {
      logAuthEvent('sign_in_error', { email, error: error.message, correlationId: loginCorrelationId })
      return { data: null, error }
    }
    logAuthEvent('sign_in_success', { email, correlationId: loginCorrelationId })
    return { data, error: null }
  }

  const signOutAndRedirect = async (path = '/auth') => {
    if (signingOut) return;
    setSigningOut(true);
    const userId = session?.user?.id
    const logoutCorrelationId = generateCorrelationId()
    logAuthEvent('sign_out_attempt', { userId, correlationId: logoutCorrelationId })

    try {
      // Handle RevenueCat logout for iOS
      if (isIOS() && IAP_ENABLED && rcConfiguredRef.current) {
        try {
          // RevenueCat.logOut() doesn't return a Promise on iOS, just call it directly
          Purchases.logOut()
          console.log('[AuthContext][RC] logOut completed')
        } catch (e) {
          console.warn('[AuthContext][RC] logOut failed', e)
        }
      }

      // Perform Supabase sign out with global scope
      await supabase.auth.signOut({ scope: 'global' });
      
      // Clear app-specific localStorage
      Object.keys(localStorage)
        .filter((k) => k.startsWith('prayai:') || k.startsWith('abiel:') || k.startsWith('prayer:'))
        .forEach((k) => localStorage.removeItem(k));
      
      // Broadcast sign-out event for cross-tab sync
      try {
        const bc = new BroadcastChannel('auth-events');
        bc.postMessage({ type: 'signed_out', ts: Date.now() });
        bc.close();
      } catch (bcError) {
        console.warn('[AuthContext] BroadcastChannel failed:', bcError);
      }

      logAuthEvent('sign_out_success', { userId, correlationId: logoutCorrelationId })
      
      // Force hard navigation to prevent cached state
      window.location.assign(path);
    } catch (error: any) {
      console.error('[AuthContext] Sign out error:', error);
      logAuthEvent('sign_out_error', { userId, error: error.message, correlationId: logoutCorrelationId })
      
      // Even if signOut fails, try a fallback signOut and force redirect
      try { 
        await supabase.auth.signOut(); 
      } catch (fallbackError) {
        console.warn('[AuthContext] Fallback signOut failed:', fallbackError);
      }
      
      // Always redirect even on error to prevent stuck sessions
      window.location.assign('/auth');
    } finally {
      setSigningOut(false);
    }
  };

  // Legacy logout method for backward compatibility
  const logout = async () => {
    await signOutAndRedirect('/auth');
    return { success: true };
  }

  // PATCHED FUNCTION BELOW
  const signInWithProvider = async (
    provider: Provider,
    additionalOptions?: { options?: { queryParams?: Record<string, string> } }
  ) => {
    if (authInProgress) {
      toast.message(`${provider} login already in progress...`)
      return { data: null, error: null }
    }

    setAuthInProgress(true)
    const onIOS = isIOS()
    const flowType: 'pkce' | 'implicit' = (onIOS && provider === 'google') ? 'implicit' : 'pkce'

    // Apple iOS native shortcut
    if (onIOS && provider === 'apple') {
      try {
        const result = await signInWithAppleSmart()
        setAuthInProgress(false)
        if (result) await refreshProfile()
        return { data: result ?? null, error: null }
      } catch (appleError: any) {
        setAuthInProgress(false)
        toast.error(appleError?.message || 'Apple Sign-In failed')
        return { data: null, error: appleError }
      }
    }

    // Persist 'state' for iOS Google to survive app switch
    let oauthState: string | undefined
    if (onIOS && provider === 'google') {
      oauthState = genState()
      setState(oauthState)
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: onIOS ? 'prayai://auth/callback' : `${window.location.origin}/auth/callback`,
        flowType,
        queryParams: {
          prompt: 'consent',
          ...(onIOS && provider === 'google' ? { response_type: 'token', state: oauthState! } : {}),
          ...additionalOptions?.options?.queryParams,
        },
        skipBrowserRedirect: onIOS,
      } as any,
    })

    if (error) {
      setAuthInProgress(false)
      toast.error(`${provider} login failed: ${error.message}`)
      return { data: null, error }
    }

    if (onIOS && data?.url) {
      try {
        await AppLauncher.openUrl({ url: data.url })
      } catch {
        try { await Browser.open({ url: data.url }) } catch { window.location.href = data.url }
      }

      // Fallback in case appUrlOpen doesn't fire (iOS quirk)
      setTimeout(async () => {
        if (signingOut) return
        try {
          const { data: sessionData } = await supabase.auth.getSession()
          if (!sessionData.session) {
            console.warn('[AuthContext][iOS] Fallback: No session after timeout.')
            toast.message('Still signing in… please tap again if nothing happens.')
            setAuthInProgress(false)
          }
        } catch (sessionError) {
          console.warn('[AuthContext][iOS] Session check failed:', sessionError)
          setAuthInProgress(false)
        }
      }, 5000)

      return { data, error: null }
    }

    if (data?.url) window.location.assign(data.url)
    setAuthInProgress(false)
    return { data, error: null }
  }

  const signup = async (email: string, password: string, userData?: Record<string, any>) => {
    logAuthEvent('sign_up_attempt', { email })
    const normalizedEmail = email.toLowerCase().trim()
    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: userData || { username: normalizedEmail.split('@')[0] },
        emailRedirectTo: 'https://prayai.aitwus.org/auth/callback',
      },
    })
    if (error) {
      logAuthEvent('sign_up_error', { email, error: error.message })
      return { data: null, error }
    }
    logAuthEvent('sign_up_success', { email })
    return { data, error: null }
  }

  const resetPassword = async (email: string) => {
    const resetCorrelationId = generateCorrelationId()
    logAuthEvent('password_reset_request', { email, correlationId: resetCorrelationId })
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: 'https://prayai.aitwus.org/auth/callback',
    })
    if (error) {
      logAuthEvent('password_reset_error', { email, error: error.message, correlationId: resetCorrelationId })
      return { success: false, error }
    }
    logAuthEvent('password_reset_success', { email, correlationId: resetCorrelationId })
    return { success: true }
  }

  const hasRole = useMemo(() => (role: Database['public']['Enums']['role_text']) =>
    roles.includes(role), [roles])

  const isAdmin = isAdminUser || hasRole('staff_admin') || hasRole('super_admin')

  return (
    <AuthContext.Provider
      value={{
        user: session?.user ?? null,
        profile,
        roles,
        isLoading,
        loading: isLoading,
        isAuthReady,
        login,
        logout,
        signOutAndRedirect,
        signingOut,
        hasRole,
        signInWithProvider,
        signup,
        resetPassword,
        isAuthenticated,
        isAdmin,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}